// An organisation's players — the customer record staff work from (spec/13 §8; F-ORG-05, F-ORG-06).
//
// One player as **this organisation** sees them: the platform profile's basics, and this
// organisation's own dealings with them — its notes, its tags, and the history tabs. Never another
// organisation's (spec/03 §11, spec/20 §6).
//
//   GET   /v1/organizations/{o}/players                          players:read  (unmasked by players:read_contact)
//   GET   /v1/organizations/{o}/players/{playerId}               players:read  (unmasked by players:read_contact)
//   PATCH /v1/organizations/{o}/players/{playerId}               players:notes
//   GET   /v1/organizations/{o}/players/{playerId}/sessions      players:read
//   GET   /v1/organizations/{o}/players/{playerId}/orders        players:read
//   GET   /v1/organizations/{o}/players/{playerId}/passes        players:read
//   GET   /v1/organizations/{o}/players/{playerId}/wallet        players:read
//   GET   /v1/organizations/{o}/players/{playerId}/points        players:read
//
// **The organisation's players are the players it has dealt with.** Search reaches them and nobody
// else — a phone number or an email address included, which is matched exactly and only among them —
// and a player the organisation has never dealt with is `404`, exactly as a player id that does not
// exist is (spec/13 §8 rule 6). The record of "dealt with" is written by the feature that deals: a
// session starting (F-SES-01), a sale (F-POS-09), a top-up. Until those land it is written by the
// seed alone, and the lists are empty in every deployment — which is what an organisation that has
// sold nothing has dealt with.
//
// **Contact details are masked by the server.** Without `players:read_contact` the phone number and
// the email address come back as `•••210` and `p•••@example.com` — enough to tell two apart, not to
// use either — in the list and on the profile alike. `contactMasked` says which the caller got.
//
// **Notes and tags are this organisation's.** Written through `PATCH` behind `players:notes`, every
// change audited with the note's text masked, and visible to nobody outside the organisation: not to
// another organisation's staff, and never to the player (spec/13 §8, F-ORG-06).
//
// **The profile carries what exists today.** Membership, tier, wallet balance, passes, owes and credit
// limit, visits, spend, favourite titles, age band and the ID check are added to it — additively — by
// the features that own them; a placeholder field typed `null` now would have to change type then,
// which is a breaking change to `/v1` (spec/08 §6). The five history tabs are here with their shape,
// and are empty until their features land (M3).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema, PlayerNoteIdSchema, SessionIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { PLAYERS_NOTES, PLAYERS_READ, PLAYERS_READ_CONTACT } from "../permissions/index.js";
import { MoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION, UNMASKED_BY_EXTENSION } from "./audit.js";
// -------------------------------------------------------------------------------------------------
// Tags and notes
// -------------------------------------------------------------------------------------------------
/** The longest tag. Tags are short labels that group people into lists (spec/13 §8). */
export const PLAYER_TAG_MAX_LENGTH = 32;
/** At most this many tags on one player, so a list stays a list. */
export const PLAYER_TAGS_MAX = 50;
/** The longest note: a paragraph, not a file. */
export const PLAYER_NOTE_MAX_LENGTH = 2000;
/** At most this many changes of each kind in one `PATCH`. */
export const PLAYER_CHANGES_MAX = 20;
/**
 * A tag as written: letters, digits, spaces, `-` and `_`, starting and ending with a letter or a
 * digit. The server stores it lower-cased with its spaces collapsed, so `Regulars` and `regulars` are
 * one tag.
 */
export const PLAYER_TAG_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9 _-]{0,30}[A-Za-z0-9])?$/;
export const PlayerTagSchema = z
    .string()
    .trim()
    .min(1)
    .max(PLAYER_TAG_MAX_LENGTH)
    .regex(PLAYER_TAG_PATTERN, 'A tag is letters, digits, spaces, - and _, and starts and ends with a letter or a digit')
    .openapi('PlayerTag', {
    description: 'A label this organisation groups players by: `regulars`, `pays cash`. Stored lower-case.',
    example: 'regulars',
});
/** What a tag becomes once stored: lower case, one space between words. */
export function normalisePlayerTag(tag) {
    return tag.trim().replaceAll(/\s+/g, ' ').toLowerCase();
}
const noteBodySchema = z.string().trim().min(1).max(PLAYER_NOTE_MAX_LENGTH);
export const PlayerNoteAuthorSchema = z
    .object({
    /** The staff member's name as it is now, or null when they have not given one. */
    name: z.string().nullable(),
})
    .openapi('PlayerNoteAuthor', { description: 'Who wrote a note.' });
export const PlayerNoteSchema = z
    .object({
    id: PlayerNoteIdSchema,
    body: z.string(),
    author: PlayerNoteAuthorSchema,
    createdAt: IsoDateTimeSchema,
    /** Later than `createdAt` once the note has been edited. */
    updatedAt: IsoDateTimeSchema,
})
    .openapi('PlayerNote', {
    description: 'A staff note about a player. This organisation’s own, and never shown to the player (F-ORG-06).',
});
// -------------------------------------------------------------------------------------------------
// The player, as this organisation sees them
// -------------------------------------------------------------------------------------------------
const organizationPlayerShape = {
    id: PlayerIdSchema,
    /** The gamer tag, when the player has chosen one. */
    username: z.string().nullable(),
    displayName: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** E.164 in full with `players:read_contact`; masked (`•••210`) without it. */
    phoneNumber: z.string().nullable(),
    /** In full with `players:read_contact`; masked (`p•••@example.com`) without it. Never a synthetic address. */
    email: z.string().nullable(),
    /** Whether `phoneNumber` and `email` were masked for this caller. */
    contactMasked: z.boolean(),
    /** This organisation's tags, alphabetical. */
    tags: z.array(z.string()),
    /** When this organisation first dealt with the player. */
    knownSince: IsoDateTimeSchema,
};
export const OrganizationPlayerSchema = z
    .object(organizationPlayerShape)
    .openapi('OrganizationPlayer', {
    description: 'A player as one organisation sees them: profile basics and its own tags (spec/13 §8).',
});
export const OrganizationPlayerListSchema = paginated(OrganizationPlayerSchema, 'OrganizationPlayerList');
// A fresh object rather than `.extend(…)`: extending a registered component emits `allOf`, which the
// client generators refuse (tools/src/codegen).
export const OrganizationPlayerProfileSchema = z
    .object({
    ...organizationPlayerShape,
    /** This organisation's notes, newest first. */
    notes: z.array(PlayerNoteSchema),
})
    .openapi('OrganizationPlayerProfile', {
    description: 'One player as one organisation sees them: profile basics, its tags and its notes (spec/13 §8). The ' +
        'fields that belong to features not built yet are added by those features.',
});
// -------------------------------------------------------------------------------------------------
// Search
// -------------------------------------------------------------------------------------------------
export const PlayerSearchQuerySchema = PageQuerySchema.extend({
    q: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .optional()
        .openapi({
        description: 'A phone number in E.164 (`+919876543210`) or an email address, matched exactly; anything else is ' +
            'matched against the name, display name and username. Only among this organisation’s players.',
    }),
    memberQr: PlayerIdSchema.optional().openapi({
        description: 'What the player’s member QR says — their player id — as the scanner read it.',
    }),
    tag: PlayerTagSchema.optional().openapi({ description: 'Only players with this tag: a list, or an audience.' }),
})
    // Unknown parameters are refused, never turned into a filter (spec/08 §2.4).
    .strict()
    .refine((query) => query.q === undefined || query.memberQr === undefined, {
    message: 'Search by text or by member QR, not both.',
    path: ['memberQr'],
})
    .openapi('PlayerSearchQuery');
// -------------------------------------------------------------------------------------------------
// Notes and tags, written (F-ORG-06)
// -------------------------------------------------------------------------------------------------
export const NewPlayerNoteSchema = z.object({ body: noteBodySchema }).strict().openapi('NewPlayerNote');
export const PlayerNoteEditSchema = z
    .object({ id: PlayerNoteIdSchema, body: noteBodySchema })
    .strict()
    .openapi('PlayerNoteEdit');
export const PlayerNoteChangesSchema = z
    .object({
    add: z.array(NewPlayerNoteSchema).max(PLAYER_CHANGES_MAX).optional(),
    edit: z.array(PlayerNoteEditSchema).max(PLAYER_CHANGES_MAX).optional(),
    remove: z.array(PlayerNoteIdSchema).max(PLAYER_CHANGES_MAX).optional(),
})
    .strict()
    .openapi('PlayerNoteChanges', { description: 'Notes to add, to edit and to remove. Each one is audited.' });
export const PlayerTagChangesSchema = z
    .object({
    add: z.array(PlayerTagSchema).max(PLAYER_CHANGES_MAX).optional(),
    remove: z.array(PlayerTagSchema).max(PLAYER_CHANGES_MAX).optional(),
})
    .strict()
    .openapi('PlayerTagChanges', {
    description: 'Tags to add and to remove. Adding one the player has, or removing one they have not, changes nothing.',
});
const changeCount = (patch) => (patch.notes?.add?.length ?? 0) +
    (patch.notes?.edit?.length ?? 0) +
    (patch.notes?.remove?.length ?? 0) +
    (patch.tags?.add?.length ?? 0) +
    (patch.tags?.remove?.length ?? 0);
export const UpdateOrganizationPlayerSchema = z
    .object({ notes: PlayerNoteChangesSchema.optional(), tags: PlayerTagChangesSchema.optional() })
    .strict()
    .refine((patch) => changeCount(patch) > 0, { message: 'Send at least one note or tag to change.' })
    .openapi('UpdateOrganizationPlayer', {
    description: 'Notes and tags — the only things an organisation writes on a player here (F-ORG-06).',
});
// -------------------------------------------------------------------------------------------------
// The history tabs (spec/13 §8). Their rows belong to features not built yet (M3); the shapes carry
// the fields those rows cannot be without, and the features add the rest.
// -------------------------------------------------------------------------------------------------
/** Paging, and nothing else: a tab that is organisation-wide has no filter to take. */
export const PlayerHistoryPageQuerySchema = PageQuerySchema.extend({}).strict().openapi('PlayerHistoryPageQuery');
/** Paging and a venue: the tabs whose rows each belong to one venue. */
export const PlayerVenueHistoryQuerySchema = PageQuerySchema.extend({
    venueId: VenueIdSchema.optional().openapi({
        description: 'Only this venue’s. A venue you do not work at is `404`, as it is in the path or in X-Clavian-Venue. ' +
            'Without it: every venue you work at.',
    }),
})
    .strict()
    .openapi('PlayerVenueHistoryQuery');
export const PlayerSessionEntrySchema = z
    .object({
    id: SessionIdSchema,
    venueId: VenueIdSchema,
    startedAt: IsoDateTimeSchema,
    /** Null while the session is running. */
    endedAt: IsoDateTimeSchema.nullable(),
    /** The PC's number at its venue, or null when the station is gone. */
    stationNumber: z.int().min(1).nullable(),
    /** `active`, `locked`, `grace` or `ended` — the session's own state. */
    status: z.string(),
    /** Seconds played so far (billed or not). */
    playedSeconds: z.int().min(0),
    /** Why it ended — `logout`, `time_up`, `staff_ended`, … — or null while it runs. */
    endReason: z.string().nullable(),
})
    .openapi('PlayerSessionEntry', { description: 'A session the player played at this organisation (F-SES).' });
export const PlayerOrderEntrySchema = z
    .object({
    id: z.string(),
    venueId: VenueIdSchema,
    placedAt: IsoDateTimeSchema,
    total: MoneySchema,
    /** Where it was placed: `counter` or `kiosk`. */
    channel: z.string(),
    /** `placed` (awaiting payment), `paid`, `refunded`, `cancelled`, … — the sale's own state. */
    status: z.string(),
    /** The sale's note, e.g. "Pass bought at the kiosk: Night Owl", or null. */
    note: z.string().nullable(),
})
    .openapi('PlayerOrderEntry', { description: 'An order (a sale) with the player on it at this organisation (F-POS).' });
export const PlayerPassEntrySchema = z
    .object({
    id: z.string(),
    name: z.string(),
    grantedSeconds: z.int().min(0),
    remainingSeconds: z.int().min(0),
    /** Null for a pass that does not expire. */
    expiresAt: IsoDateTimeSchema.nullable(),
    /** `active`, `exhausted`, `expired`, `refunded` or `voided` — the holding's own state. */
    status: z.string(),
    purchasedAt: IsoDateTimeSchema,
})
    .openapi('PlayerPassEntry', { description: 'A pass the player holds or held at this organisation (F-PRC).' });
export const PlayerWalletEntrySchema = z
    .object({
    id: z.string(),
    occurredAt: IsoDateTimeSchema,
    /** Signed: a top-up is positive, a spend negative. */
    amount: MoneySchema,
    description: z.string(),
})
    .openapi('PlayerWalletEntry', { description: 'A line of the player’s wallet statement here (F-WAL-08).' });
export const PlayerPointsEntrySchema = z
    .object({
    id: z.string(),
    occurredAt: IsoDateTimeSchema,
    /** Where they were earned, when it was at a venue. */
    venueId: VenueIdSchema.nullable(),
    /** Signed. */
    points: z.int(),
})
    .openapi('PlayerPointsEntry', { description: 'Points the player earned at this organisation. Filled by F-LOY (M3).' });
export const PlayerSessionListSchema = paginated(PlayerSessionEntrySchema, 'PlayerSessionList');
export const PlayerOrderListSchema = paginated(PlayerOrderEntrySchema, 'PlayerOrderList');
export const PlayerPassListSchema = paginated(PlayerPassEntrySchema, 'PlayerPassList');
export const PlayerWalletListSchema = paginated(PlayerWalletEntrySchema, 'PlayerWalletList');
export const PlayerPointsListSchema = paginated(PlayerPointsEntrySchema, 'PlayerPointsList');
// -------------------------------------------------------------------------------------------------
// The routes
// -------------------------------------------------------------------------------------------------
const PlayerParamsSchema = OrganizationScopeParamsSchema.extend({ playerId: PlayerIdSchema }).openapi('PlayerParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const playerNotFound = problemResponse('No such organisation, or no such player among its players — a player it has never dealt with included.', 'not-found');
const jsonBody = (schema) => ({
    body: { content: { [JSON_CONTENT_TYPE]: { schema } }, required: true },
});
const readResponses = (schema, description) => ({
    200: { description, content: { [JSON_CONTENT_TYPE]: { schema } } },
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: forbidden,
    404: playerNotFound,
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
});
export const listPlayersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players',
    operationId: 'listPlayers',
    tags: ['players'],
    summary: 'Search the organisation’s players',
    description: 'This organisation’s players only — the ones it has dealt with — by name, username, phone, email, member QR ' +
        'or tag, oldest first. Contact details are masked without `players:read_contact`. Requires `players:read`.',
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    [UNMASKED_BY_EXTENSION]: PLAYERS_READ_CONTACT,
    request: { params: OrganizationScopeParamsSchema, query: PlayerSearchQuerySchema },
    responses: readResponses(OrganizationPlayerListSchema, 'A page of players.'),
});
export const getPlayerRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}',
    operationId: 'getPlayer',
    tags: ['players'],
    summary: 'One player, as this organisation sees them',
    description: 'Profile basics, this organisation’s tags and notes. A player it has never dealt with is `404`. Contact ' +
        'details are masked without `players:read_contact`. Requires `players:read`.',
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    [UNMASKED_BY_EXTENSION]: PLAYERS_READ_CONTACT,
    request: { params: PlayerParamsSchema },
    responses: readResponses(OrganizationPlayerProfileSchema, 'The player.'),
});
export const updatePlayerRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/players/{playerId}',
    operationId: 'updatePlayer',
    tags: ['players'],
    summary: 'Add, edit or remove notes and tags',
    description: 'Every note added, edited or removed writes one audit row, with the text masked; a change to the tags writes ' +
        'one with the tags before and after. All of it or none of it. Requires `players:notes`.',
    [PERMISSION_EXTENSION]: PLAYERS_NOTES,
    [UNMASKED_BY_EXTENSION]: PLAYERS_READ_CONTACT,
    request: { params: PlayerParamsSchema, ...jsonBody(UpdateOrganizationPlayerSchema) },
    responses: {
        ...readResponses(OrganizationPlayerProfileSchema, 'The player, with the change.'),
        404: problemResponse('No such player among the organisation’s players, or no such note of theirs.', 'not-found'),
    },
});
const historyResponses = (list) => readResponses(list, 'A page of the tab.');
const historyDescription = (filledBy) => `This organisation’s only, newest first. Empty until ${filledBy} lands. Requires \`players:read\`.`;
export const listPlayerSessionsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/sessions',
    operationId: 'listPlayerSessions',
    tags: ['players'],
    summary: 'The player’s sessions here',
    description: historyDescription('F-SES (M3)'),
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerParamsSchema, query: PlayerVenueHistoryQuerySchema },
    responses: historyResponses(PlayerSessionListSchema),
});
export const listPlayerOrdersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/orders',
    operationId: 'listPlayerOrders',
    tags: ['players'],
    summary: 'The player’s orders here',
    description: historyDescription('F-POS (M3)'),
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerParamsSchema, query: PlayerVenueHistoryQuerySchema },
    responses: historyResponses(PlayerOrderListSchema),
});
export const listPlayerPassesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/passes',
    operationId: 'listPlayerPasses',
    tags: ['players'],
    summary: 'The player’s passes here',
    description: historyDescription('F-PRC (M3)'),
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerParamsSchema, query: PlayerHistoryPageQuerySchema },
    responses: historyResponses(PlayerPassListSchema),
});
export const listPlayerWalletRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/wallet',
    operationId: 'listPlayerWallet',
    tags: ['players'],
    summary: 'The player’s wallet statement here',
    description: historyDescription('F-WAL (M3)'),
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerParamsSchema, query: PlayerHistoryPageQuerySchema },
    responses: historyResponses(PlayerWalletListSchema),
});
export const listPlayerPointsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/players/{playerId}/points',
    operationId: 'listPlayerPoints',
    tags: ['players'],
    summary: 'The points the player earned here',
    description: historyDescription('F-LOY (M3)'),
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    request: { params: PlayerParamsSchema, query: PlayerHistoryPageQuerySchema },
    responses: historyResponses(PlayerPointsListSchema),
});
export const PLAYER_ROUTES = [
    listPlayersRoute,
    getPlayerRoute,
    updatePlayerRoute,
    listPlayerSessionsRoute,
    listPlayerOrdersRoute,
    listPlayerPassesRoute,
    listPlayerWalletRoute,
    listPlayerPointsRoute,
];
//# sourceMappingURL=players.js.map